'use-client';

import type { ModalProps as NextModalProps } from '@heroui/react';
import { Button, Modal as NextModal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect } from 'react';
export type ModalProps = Omit<NextModalProps, 'children'> & {
  header?: ReactNode;
  onAction?: () => void;
  onSecondAction?: () => void;
  onClickLeftButton?: () => void;
  actionText?: string;
  closeText?: string;
  secondText?: string;
  leftButtonText?: ReactNode;
  showActionButton?: boolean;
  showCloseButton?: boolean;
  showSecondButton?: boolean;
  showLeftButton?: boolean;
  showFooter?: boolean;
  loading?: boolean;
  loadingSecondButton?: boolean;
  disabled?: boolean;
  disableScroll?: boolean;
  footer?: ReactNode;
  form?: string;
  children?: ReactNode;
};
const Modal = ({
  ...props
}: ModalProps) => {
  const {
    header,
    showFooter = true,
    footer,
    children,
    actionText,
    closeText,
    secondText,
    leftButtonText,
    showActionButton = true,
    showCloseButton = true,
    showSecondButton = false,
    showLeftButton = false,
    loading = false,
    loadingSecondButton = false,
    form,
    onClose,
    onSubmit,
    onAction,
    onSecondAction,
    onClickLeftButton,
    className = '',
    classNames,
    disabled = false,
    disableScroll = true,
    placement = 'center',
    ...rest
  } = props;
  const t = useTranslations();
  useEffect(() => {
    const html = document.getElementsByTagName('html')[0];
    if (html && disableScroll) {
      if (props.isOpen) {
        html.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      } else {
        html.style.overflow = '';
        document.body.style.overflow = '';
      }
      return () => {
        html.style.overflow = '';
        document.body.style.overflow = '';
      };
    }
  }, [props.isOpen, disableScroll]);
  return <NextModal onClose={onClose} classNames={{
    closeButton: 'm-2 z-10',
    base: 'overflow-visible m-0 flex max-h-dvh flex-col md:max-h-[96dvh]',
    header: 'flex flex-col p-0',
    wrapper: 'overflow-hidden',
    ...classNames
  }} shouldBlockScroll={false} className={className} placement={placement} {...rest} data-sentry-element="NextModal" data-sentry-component="Modal" data-sentry-source-file="index.tsx">
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        {onClose => <>
            {header && <ModalHeader>{header}</ModalHeader>}
            <ModalBody className='flex-1 overflow-auto p-0'>
              {children}
            </ModalBody>
            {showFooter && (footer ? footer : <ModalFooter>
                  {showLeftButton ? <Button
          // color='primary'
          variant='flat' onPress={onClickLeftButton} className='mr-auto'>
                      {leftButtonText || 'View'}
                    </Button> : null}
                  {showCloseButton ? <Button color='default' variant='light' onPress={onClose}>
                      {closeText || t('app.cancel')}
                    </Button> : null}
                  {showSecondButton ? <Button color='danger' variant='flat' isLoading={loadingSecondButton} onPress={onSecondAction}>
                      {secondText || t('app.delete')}
                    </Button> : null}
                  {showActionButton ? <Button className={`${disabled ? 'cursor-not-allowed bg-gray-300' : 'bg-gradient-to-r from-primary-3 to-primary-2 '} text-white`} onSubmit={onSubmit} onPress={disabled ? () => {} : onAction} isLoading={loading} disabled={disabled} type={form ? 'submit' : 'button'} form={form}>
                      {actionText || t('app.submit')}
                    </Button> : null}
                </ModalFooter>)}
          </>}
      </ModalContent>
    </NextModal>;
};
export default Modal;